/**
 * The two things a sandboxed app needs to run its own repo (RFC 0275).
 *
 * Both travel as DATA because the frame is a srcdoc document at an opaque
 * origin: it cannot fetch, cannot resolve a bare specifier, and cannot be handed
 * a live object. A compiled `WebAssembly.Module` cannot cross either, which was
 * measured rather than assumed: sent to the same frame, a control message
 * arrived and the module message was silently dropped, with no throw on either
 * side. So the host ships source text and wasm bytes, and each frame compiles
 * its own (~3.2MB, ~50ms).
 *
 * Fetched ONCE per host and cached. The frames pay memory per instance; they
 * should not also pay a fetch each, and a republish remounts often enough that
 * a per-frame fetch would be visible.
 */

let cached: Promise<{ runtime: string; wasm: Uint8Array }> | null = null

export function loadGuestAssets(): Promise<{
  runtime: string
  wasm: Uint8Array
}> {
  if (cached) return cached
  cached = (async () => {
    const [runtime, wasm] = await Promise.all([
      // Built by scripts/build-guest-runtime.mjs into public/. Read as TEXT:
      // the guest blob-imports it, so it must not be a module the host resolves.
      fetch('/mod-guest-runtime.js').then((r) => {
        if (!r.ok) {
          throw new Error(
            'mod-guest-runtime.js is missing — run ' +
              '`node scripts/build-guest-runtime.mjs` in packages/app',
          )
        }
        return r.text()
      }),
      import('@automerge/automerge/automerge.wasm?url')
        .then((m) => fetch((m as { default: string }).default))
        .then((r) => r.arrayBuffer())
        .then((b) => new Uint8Array(b)),
    ])
    return { runtime, wasm }
  })()
  // A failed fetch must not poison every later frame with the same rejection.
  cached.catch(() => {
    cached = null
  })
  return cached
}
